import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, Facebook, Instagram, Linkedin, Twitter, Link2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_ICONS = {
  facebook: { Icon: Facebook, color: "#1877F2", label: "Facebook" },
  instagram: { Icon: Instagram, color: "#E4405F", label: "Instagram" },
  linkedin: { Icon: Linkedin, color: "#0A66C2", label: "LinkedIn" },
  twitter: { Icon: Twitter, color: "#1DA1F2", label: "Twitter / X" },
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    travel_date: "",
    pax: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [adminContact, setAdminContact] = useState({ email: "", phone: "", address: "", social_links: [] });

  useEffect(() => {
    axios.get(`${API}/public/admin-contact`)
      .then(res => setAdminContact(res.data || {}))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        pax: formData.pax ? parseInt(formData.pax) : null
      });
      setSubmitted(true);
      toast.success("Your inquiry has been submitted successfully!");
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: "Office Address", content: adminContact.address || "123 Travel Street, Holiday City, India - 110001" },
    { icon: Phone, title: "Phone Number", content: adminContact.phone || "+91 8699489969", link: `tel:${(adminContact.phone || "+918699489969").replace(/\s+/g, '')}` },
    { icon: Mail, title: "Email Address", content: adminContact.email || "info@happyholidaysdmc.com", link: `mailto:${adminContact.email || "info@happyholidaysdmc.com"}` },
    { icon: Clock, title: "Business Hours", content: "Mon - Sat: 9:00 AM - 7:00 PM" },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-primary-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-secondary-500 font-accent text-xl">Get in Touch</span>
            <h1 className="font-heading text-4xl md:text-6xl text-white mt-2 mb-6">
              Contact Us
            </h1>
            <p className="text-white/80 max-w-2xl mx-auto">
              Have questions or need a custom quote? Our team is here to help you plan the perfect journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <span className="text-secondary-500 font-accent text-lg">Contact Info</span>
                <h2 className="font-heading text-2xl mt-1 mb-6">Let's Connect</h2>
              </div>
              
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-4 p-4 bg-white rounded-lg shadow-sm"
                >
                  <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    {item.link ? (
                      <a href={item.link} className="text-muted-foreground hover:text-primary-700 transition-colors">
                        {item.content}
                      </a>
                    ) : (
                      <p className="text-muted-foreground text-sm">{item.content}</p>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Social Links */}
              {adminContact.social_links && adminContact.social_links.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="p-4 bg-white rounded-lg shadow-sm"
                  data-testid="contact-social-links"
                >
                  <h3 className="font-semibold mb-3">Follow Us</h3>
                  <div className="flex flex-wrap gap-3">
                    {adminContact.social_links.filter(l => l && l.url).map((link, idx) => {
                      const meta = SOCIAL_ICONS[link.platform] || { Icon: Link2, color: "#64748b", label: link.platform };
                      const Icon = meta.Icon;
                      return (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={meta.label}
                          className="w-11 h-11 rounded-full bg-gray-50 hover:bg-white border border-gray-200 hover:shadow-md flex items-center justify-center transition-all hover:-translate-y-0.5"
                          style={{ color: meta.color }}
                          data-testid={`social-link-${link.platform}`}
                        >
                          <Icon className="w-5 h-5" />
                        </a>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* WhatsApp CTA */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-[#25D366] rounded-lg text-white"
              >
                <h3 className="font-semibold mb-2">Quick Response on WhatsApp</h3>
                <p className="text-white/90 text-sm mb-4">
                  Get instant replies by messaging us on WhatsApp!
                </p>
                <a 
                  href="https://wa.me/918699489969" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-[#25D366] px-4 py-2 rounded-md font-medium hover:bg-white/90 transition-colors"
                >
                  Chat on WhatsApp
                </a>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-8"
              >
                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="font-heading text-2xl mb-2">Thank You!</h3>
                    <p className="text-muted-foreground mb-6">
                      Your inquiry has been received. Our team will contact you within 24 hours.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Submit Another Inquiry
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      <span className="text-secondary-500 font-accent text-lg">Inquiry Form</span>
                      <h2 className="font-heading text-2xl mt-1">Send Us a Message</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name">Full Name *</Label>
                          <Input 
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            data-testid="contact-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input 
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            data-testid="contact-email"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input 
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            data-testid="contact-phone"
                          />
                        </div>
                        <div>
                          <Label htmlFor="destination">Destination (Optional)</Label>
                          <Input 
                            id="destination"
                            name="destination"
                            value={formData.destination}
                            onChange={handleChange}
                            placeholder="e.g., Maldives, Switzerland"
                            data-testid="contact-destination"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="travel_date">Travel Date (Optional)</Label>
                          <Input 
                            id="travel_date"
                            name="travel_date"
                            type="date"
                            value={formData.travel_date}
                            onChange={handleChange}
                            data-testid="contact-date"
                          />
                        </div>
                        <div>
                          <Label htmlFor="pax">Number of Travelers</Label>
                          <Input 
                            id="pax"
                            name="pax"
                            type="number"
                            min="1"
                            value={formData.pax}
                            onChange={handleChange}
                            placeholder="e.g., 4"
                            data-testid="contact-pax"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea 
                          id="message"
                          name="message"
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Tell us about your travel requirements..."
                          data-testid="contact-message"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="w-full bg-primary-700 hover:bg-primary-900"
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Inquiry
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
